import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { PopupService } from '../../shared/modal/popup.service';
import { TranslateService } from '@ngx-translate/core';
import { AlertService } from '../../shared/alert/alert.service';
import { VariableDetails } from '../../shared/ontology/model/variable-details';
import { HttpResponse } from '@angular/common/http';
import { VariableTypeEnum } from '../../shared/ontology/variable-type.enum';
import { formatErrorList } from '../../shared/alert/format-error-list';
import { LotImportUpdateDescriptorsConfirmationDialogComponent } from './lot-import-update-descriptors-confirmation-dialog.component';
import { VariableService } from '../../shared/ontology/service/variable.service';
import { VariableValidationService } from '../../shared/ontology/service/variable-validation.service';
import { FileDownloadHelper } from '../sample/file-download.helper';
import { JhiAlertService, JhiEventManager } from '../../shared/core-utils';
import { parseFile } from '../../shared/util/file-utils';
import { InventoryService } from '../../shared/inventory/service/inventory.service';
import { LotTemplateExportType } from '../../shared/inventory/service/lot-template-export-type';
import { LotService } from '../../shared/inventory/service/lot.service';
import { SubscriptionsManager } from '../../shared/util/subscriptions-manager';

const SUCCESS = 'inventoryManagerApp.lot.import.update.success';

@Component({
    selector: 'jhi-lot-import-update-dialog',
    templateUrl: './lot-import-update-dialog.component.html'
})
export class LotImportUpdateDialogComponent implements OnInit, OnDestroy {

    @ViewChild('fileUpload')
        fileUpload: ElementRef;

    fileName = '';
    importData = new Array<any>();

    importFormats = [
        { name: 'CSV', extension: '.csv' }, //
        { name: 'Excel', extension: '.xls,.xlsx' }
    ];

    header: Array<string>;
    data: Array<any>;
    attributes: VariableDetails[] = [];
    stockIDMapping;
    lotUIDMapping;
    unitMapping;
    storageLocationAbbrMapping;
    notesMapping;
    isProcessing = false;
    subscriptionsManager = new SubscriptionsManager();

    requiredHeaders = ['STOCK ID', 'LOT UID', 'STORAGE LOCATION ABBR', 'UNITS', 'NOTES'];

    selectedFileType = this.importFormats[1].extension; // '.xlsx'; // Set the default file type to CSV.

    constructor(
        private alertService: AlertService,
        public activeModal: NgbActiveModal,
        private modalService: NgbModal,
        private lotService: LotService,
        private fileDownloadHelper: FileDownloadHelper,
        private eventManager: JhiEventManager,
        private translateService: TranslateService,
        private variableService: VariableService,
        private variableValidationService: VariableValidationService) {

    }

    ngOnDestroy(): void {
        this.subscriptionsManager.unsubscribeAll();
    }

    ngOnInit(): void {
    }

    close() {
        this.activeModal.dismiss('cancel');
    }

    import() {
        if (this.validateFile()) {
            this.header = this.importData[0].map(this.toUpper);

            // Convert rawData into map
            this.data = this.importData.slice(1).map((fileRow) => {
                return fileRow.reduce((map, col, colIndex) => {
                    map[this.header [colIndex]] = col;
                    return map;
                }, {});
            });

            this.stockIDMapping = this.mappingHeader(this.importData[0], 'Stock ID');
            this.lotUIDMapping = this.mappingHeader(this.importData[0], 'Lot UID');
            this.unitMapping = this.mappingHeader(this.importData[0], 'Units');
            this.storageLocationAbbrMapping = this.mappingHeader(this.importData[0], 'Storage Location Abbr');
            this.notesMapping = this.mappingHeader(this.importData[0], 'Notes');
            this.proceed();
        }
    }

    mappingHeader(header: Array<string>, mapping: string) {
        for (const column of header) {
            if (column.toLowerCase() === mapping.toLowerCase()) {
                return column;
            }
        }
        return '';
    }

    clearSelectedFile(): void {
        this.importData = null;
    }

    onFileChange(evt: any) {
        const target = evt.target;
        this.fileName = target.files[0].name;

        const extension = this.fileName.split('.');
        if (extension[1].toLowerCase() !== 'xls' && extension[1].toLowerCase() !== 'xlsx' && extension[1].toLowerCase() !== 'csv') {
            this.fileName = '';
            target.value = '';
            this.alertService.error('inventoryManagerApp.error.import.lot.update.invalid.file.ext.csv');
            return;
        }

        this.subscriptionsManager.add = parseFile(target.files[0], '').subscribe((value) => {
            this.importData = value;
        });
    }

    private validateFile() {
        if (!this.importData || this.importData.filter((row) => row.some((column) => Boolean(column.trim()))).length <= 1) {
            this.alertService.error('error.import.file.empty');
            return false;
        }

        const headers = this.importData[0].map(this.toUpper);

        if (!this.requiredHeaders.every((r) => headers.includes(r))) {
            const errorMessage: string[] = [];
            errorMessage.push(this.translateService.instant('error.import.header.mandatory', { param: this.requiredHeaders.join(', ') }));
            this.alertService.error('error.custom', { param: formatErrorList(errorMessage) });
            return false;
        }

        return true;
    }

    async proceed() {

        this.isProcessing = true;
        const isValidAttributes = await this.processAttributes();
        if (!isValidAttributes) {
            this.isProcessing = false;
            return;
        }
        try {
            await this.confirmAttributeStatus();
        } catch (e) {
            this.isProcessing = false;
            return;
        }

        const lotUpdates = this.importData
            .slice(1) // skip header
            .filter((row) => row.some((column) => Boolean(column.trim()))) // remove empty rows
            .map((row, index) => {
                return {
                    stockID: row[this.getColumnIndex(this.stockIDMapping)],
                    lotUID: row[this.getColumnIndex(this.lotUIDMapping)],
                    storageLocationAbbr: row[this.getColumnIndex(this.storageLocationAbbrMapping)],
                    unitName: row[this.getColumnIndex(this.unitMapping)],
                    notes: row[this.getColumnIndex(this.notesMapping)],
                    attributes: this.generateAttributesValuesMap(this.data[index], this.attributes)
                };
            });

        const lotsUpdateRequest = {
            'multiInput': {
                'lotList': lotUpdates
            }
        };

        this.subscriptionsManager.add = this.lotService.update(lotsUpdateRequest).subscribe(
            (res: HttpResponse<any>) =>
                this.onSaveSuccess(res));
    }

    async processAttributes() {
        // Anything outside expected headers are considered codes.
        const codes: string[] = this.header.filter((column) =>
            !(<any>Object).values([this.stockIDMapping, this.lotUIDMapping, this.unitMapping, this.storageLocationAbbrMapping,
                this.notesMapping, '']).includes(column)
        );

        // Determine which of the codes are attributes
        this.attributes = await this.variableService.filterVariables({
            variableNames: codes,
            variableTypeIds: [VariableTypeEnum.INVENTORY_ATTRIBUTE.toString()],
            showObsoletes: true
        }).toPromise();

        const errorMessage: string[] = [];
        this.validateHeader(errorMessage, codes, this.attributes);

        if (errorMessage.length !== 0) {
            this.alertService.error('error.custom', { param: formatErrorList(errorMessage) });
            return false;
        }

        return true;
    }

    private validateHeader(errorMessage: string[], codes: string[], attributes: VariableDetails[]) {
        const duplicateColumns = Array.from(new Set(this.header.filter((header, i) => this.header.indexOf(header) !== i)));
        if (duplicateColumns.length >= 1) {
            errorMessage.push(this.translateService.instant('error.import.header.duplicated', { param: duplicateColumns.join(', ') }));
        }

        const invalidCodes = codes.filter((code) =>
            attributes.every((attribute) => this.toUpper(attribute.alias) !== code && this.toUpper(attribute.name) !== code)
        );
        if (invalidCodes && invalidCodes.length > 0) {
            errorMessage.push(this.translateService.instant('inventoryManagerApp.error.import.invalid.codes', { param: invalidCodes.join(', ') }));
        }
    }

    private async confirmAttributeStatus() {
        const attributeStatusById = this.variableValidationService.computeAttributeStatus(this.attributes, this.data);
        const invalidAttributeIds = Object.entries(attributeStatusById).filter((entry) => entry[1])
            .map((entry) => entry[0]);

        if (invalidAttributeIds.length) {
            const invalidAttributes = this.attributes.filter((attribute) => invalidAttributeIds.includes(attribute.id));
            const modalRef = this.modalService.open(LotImportUpdateDescriptorsConfirmationDialogComponent,
                { size: 'lg', backdrop: 'static' });
            modalRef.componentInstance.attributeStatusById = attributeStatusById;
            modalRef.componentInstance.attributes = invalidAttributes;
            return modalRef.result;
        }
        return true;
    }

    toUpper(param: string) {
        return (param || '').toUpperCase();
    }

    generateAttributesValuesMap(rowData: string[], attributes: VariableDetails[]) {
        const attributesValuesMap = {};
        attributes.forEach((attribute) => {
            if (rowData[this.toUpper(attribute.name)]) {
                attributesValuesMap[this.toUpper(attribute.name)] = rowData[this.toUpper(attribute.name)];
            } else if (rowData[this.toUpper(attribute.alias)]) {
                attributesValuesMap[this.toUpper(attribute.alias)] = rowData[this.toUpper(attribute.alias)];
            }
        });
        return attributesValuesMap;
    }

    private getColumnIndex(columnName) {
        return this.header.indexOf(columnName);
    }

    private onSaveSuccess(lotUUIDs) {
        this.alertService.success(SUCCESS);
        this.eventManager.broadcast({ name: 'columnFiltersChanged', content: { filterBy: { lotUUIDs } } });
        this.activeModal.dismiss('cancel');
    }

    export($event) {
        $event.preventDefault();
        this.subscriptionsManager.add = this.lotService.downloadLotsTemplate(LotTemplateExportType.LOT_UPDATE).subscribe((response) => {

            const fileName = this.fileDownloadHelper.getFileNameFromResponseContentDisposition(response);
            this.fileDownloadHelper.save(response.body, fileName);

        });
    }
}

@Component({
    selector: 'jhi-lot-import-update-popup',
    template: ''
})
export class LotImportUpdatePopupComponent implements OnInit, OnDestroy {

    routeSub: any;

    constructor(private inventoryService: InventoryService,
                private jhiAlertService: JhiAlertService,
                private route: ActivatedRoute,
                private popupService: PopupService
    ) {
    }

    ngOnInit() {
        this.routeSub = this.route.params.subscribe((params) => {
            this.popupService
                .open(LotImportUpdateDialogComponent as Component);
        });
    }

    ngOnDestroy() {
        this.routeSub.unsubscribe();
    }

}
